import { useCallback, useEffect, useState } from "react";

import {
  getStock,
  getWarehouses,
  triggerStockSync,
} from "../../api/client.js";
import { useManualSync } from "../../hooks/useManualSync.js";
import { getNextTableSort, useTableSort } from "../../hooks/useTableSort.js";
import { Icon } from "../Icon.jsx";
import { SortableHeader } from "../SortableHeader.jsx";
import {
  DataMasterActiveFilters,
  DataMasterKpiCard,
  DataMasterKpiStrip,
  DataMasterPagination,
  DataMasterSection,
  DataMasterTableShell,
  DataMasterToolbar,
  SyncStatusStrip,
} from "./DataMasterLayout.jsx";

const quantityFormatter = new Intl.NumberFormat("id-ID", {
  maximumFractionDigits: 4,
});

const STOCK_STATUS_LABELS = {
  AVAILABLE: "Stok tersedia",
  ZERO: "Stok nol",
  NEGATIVE: "Stok negatif",
};

function createStockFilters() {
  return {
    search: "",
    warehouseId: "",
    quantityStatus: "",
  };
}

function formatTimestamp(value) {
  if (!value) return "Belum pernah";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Belum pernah";

  return new Intl.DateTimeFormat("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
}

function latestSyncResult(syncRun) {
  if (!syncRun) return {};
  if (syncRun.result) return syncRun.result;

  try {
    return syncRun.result_json ? JSON.parse(syncRun.result_json) : {};
  } catch {
    return {};
  }
}

function syncStatusLabel(syncRun) {
  const status = syncRun?.status;
  if (["QUEUED", "RUNNING"].includes(status)) return "Sinkronisasi sedang berjalan";
  if (status === "SUCCESS") return "Sinkronisasi berhasil";
  if (status === "PARTIAL_SUCCESS") return "Sinkronisasi selesai sebagian";
  if (status === "FAILED") return "Sinkronisasi gagal";
  return "Belum pernah sinkron";
}

function stockSyncMetrics(syncRun) {
  const result = latestSyncResult(syncRun);
  const errorCount = Array.isArray(result.errors)
    ? result.errors.length
    : Number(syncRun?.error_count || 0);

  return [
    {
      label: "baris",
      value: quantityFormatter.format(
        Number(result.rowsInserted ?? syncRun?.records_upserted ?? 0),
      ),
    },
    {
      label: "gudang",
      value: quantityFormatter.format(Number(result.warehousesProcessed || 0)),
    },
    { label: "error", value: quantityFormatter.format(errorCount) },
  ];
}

function stockState(quantity) {
  const numeric = Number(quantity || 0);
  if (numeric < 0) return "NEGATIVE";
  if (numeric > 0) return "AVAILABLE";
  return "ZERO";
}

function StockStatusBadge({ quantity }) {
  const state = stockState(quantity);

  return (
    <span className={`dm-stock-state dm-stock-state--${state.toLowerCase()}`}>
      {STOCK_STATUS_LABELS[state]}
    </span>
  );
}

export function StockTab() {
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [summary, setSummary] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    pageCount: 1,
    rowCount: 0,
  });
  const [draftFilters, setDraftFilters] = useState(createStockFilters);
  const [filters, setFilters] = useState(createStockFilters);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { sort, setSort } = useTableSort("skuCode");

  const load = useCallback(
    async (
      page = 1,
      nextFilters = filters,
      nextSort = sort,
      nextPageSize = pagination.pageSize,
    ) => {
      setLoading(true);
      setError("");

      try {
        const payload = await getStock({
          page,
          pageSize: nextPageSize,
          ...nextFilters,
          sortBy: nextSort.key,
          sortDir: nextSort.direction,
        });

        setItems(payload.items || []);
        setSnapshot(payload.snapshot);
        setSummary(payload.summary || {});
        setPagination(payload.pagination);
        sync.setSyncRun(payload.latestSync);
      } catch (requestError) {
        setError(requestError.message);
      } finally {
        setLoading(false);
      }
    },
    [filters, pagination.pageSize, sort],
  );

  const sync = useManualSync(triggerStockSync, async () => {
    await load(1, filters, sort);
  });

  useEffect(() => {
    const initialFilters = createStockFilters();

    Promise.all([getWarehouses(), load(1, initialFilters)])
      .then(([warehousePayload]) =>
        setWarehouses(warehousePayload.warehouses || []),
      )
      .catch((requestError) => setError(requestError.message));
  }, []);

  function updateDraftFilter(key, value) {
    setDraftFilters((current) => ({ ...current, [key]: value }));
  }

  function submitFilters(event) {
    event.preventDefault();
    const nextFilters = {
      ...draftFilters,
      search: draftFilters.search.trim(),
    };
    setFilters(nextFilters);
    load(1, nextFilters, sort);
  }

  function clearFilters() {
    const nextFilters = createStockFilters();
    setDraftFilters(nextFilters);
    setFilters(nextFilters);
    load(1, nextFilters, sort);
  }

  function toggleSort(column) {
    const nextSort = getNextTableSort(sort, column);
    setSort(nextSort);
    load(1, filters, nextSort);
  }

  function changePageSize(pageSize) {
    load(1, filters, sort, pageSize);
  }

  const warehouseLabel = warehouses.find(
    (warehouse) => String(warehouse.warehouseId) === String(filters.warehouseId),
  )?.warehouseName;

  return (
    <DataMasterSection
      error={error || sync.syncError}
      subtitle="Pantau snapshot stok terakhir dari seluruh gudang Accurate dan identifikasi ketersediaan barang."
      title="Stok"
    >
      <DataMasterKpiStrip label="Ringkasan snapshot stok">
        <DataMasterKpiCard
          helper="Sesuai filter aktif"
          icon="database"
          label="Total record"
          value={summary.totalRecords}
        />
        <DataMasterKpiCard
          helper="Kode barang unik"
          icon="orders"
          label="SKU unik"
          tone="info"
          value={summary.uniqueSkus}
        />
        <DataMasterKpiCard
          helper="Gudang tercakup"
          icon="master"
          label="Gudang"
          value={summary.warehouses}
        />
        <DataMasterKpiCard
          helper="Record dengan qty > 0"
          icon="check"
          label="Stok tersedia"
          tone="success"
          value={summary.available}
        />
        <DataMasterKpiCard
          helper={
            Number(summary.negative || 0) > 0
              ? `${quantityFormatter.format(summary.negative)} record negatif`
              : "Record dengan qty 0"
          }
          icon="warning"
          label="Stok nol"
          tone={Number(summary.negative || 0) > 0 ? "danger" : "warning"}
          value={summary.zero}
        />
      </DataMasterKpiStrip>

      <SyncStatusStrip
        actions={
          <button
            className="primary-button"
            disabled={sync.syncing}
            onClick={sync.startSync}
            type="button"
          >
            <span className={sync.syncing ? "is-spinning" : ""}>
              <Icon name="refresh" size={15} />
            </span>
            {sync.syncing ? "Menyinkronkan…" : "Sinkronkan Stok"}
          </button>
        }
        lastSynced={formatTimestamp(
          snapshot?.completedAt ||
            sync.syncRun?.completed_at ||
            sync.syncRun?.updated_at,
        )}
        metrics={stockSyncMetrics(sync.syncRun)}
        progress={
          ["QUEUED", "RUNNING"].includes(sync.syncRun?.status)
            ? `${quantityFormatter.format(
                Number(sync.syncRun?.records_seen || 0),
              )} baris telah dibaca`
            : ""
        }
        status={syncStatusLabel(sync.syncRun)}
        title="Sumber: Accurate · Snapshot seluruh gudang aktif"
      />

      <DataMasterToolbar className="dm-toolbar--stock" onSubmit={submitFilters}>
        <Icon name="search" size={15} />
        <input
          aria-label="Cari stok"
          onChange={(event) => updateDraftFilter("search", event.target.value)}
          placeholder="Cari SKU atau nama barang"
          value={draftFilters.search}
        />
        <select
          aria-label="Gudang"
          onChange={(event) => updateDraftFilter("warehouseId", event.target.value)}
          value={draftFilters.warehouseId}
        >
          <option value="">Semua gudang</option>
          {warehouses
            .filter((warehouse) => warehouse.active)
            .map((warehouse) => (
              <option key={warehouse.warehouseId} value={warehouse.warehouseId}>
                {warehouse.warehouseName}
              </option>
            ))}
        </select>
        <select
          aria-label="Status stok"
          onChange={(event) => updateDraftFilter("quantityStatus", event.target.value)}
          value={draftFilters.quantityStatus}
        >
          <option value="">Semua status stok</option>
          {Object.entries(STOCK_STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <button className="primary-button" disabled={loading} type="submit">
          <Icon name="filter" size={14} />
          Terapkan
        </button>
        <button className="text-button" disabled={loading} onClick={clearFilters} type="button">
          Bersihkan
        </button>
      </DataMasterToolbar>

      <DataMasterActiveFilters
        filters={[
          { label: "Pencarian", value: filters.search },
          { label: "Gudang", value: warehouseLabel },
          { label: "Status", value: STOCK_STATUS_LABELS[filters.quantityStatus] },
        ]}
        onClear={clearFilters}
      />

      <DataMasterTableShell
        className="dm-table-shell--stock dm-table-shell--accurate"
        emptyText="Tidak ada data stok yang sesuai dengan filter aktif."
        loading={loading}
        rowCount={items.length}
      >
        <table className="master-table master-table--stock dm-table--fixed">
          <colgroup>
            <col className="dm-col-item" />
            <col className="dm-col-name" />
            <col className="dm-col-number" />
            <col className="dm-col-status" />
            <col className="dm-col-date" />
          </colgroup>
          <thead>
            <tr>
              <SortableHeader column="skuCode" sort={sort} toggleSort={toggleSort}>
                Barang
              </SortableHeader>
              <SortableHeader column="warehouseName" sort={sort} toggleSort={toggleSort}>
                Gudang
              </SortableHeader>
              <SortableHeader column="quantity" sort={sort} toggleSort={toggleSort}>
                Kuantitas
              </SortableHeader>
              <th>Status stok</th>
              <SortableHeader column="syncedAt" sort={sort} toggleSort={toggleSort}>
                Terakhir sinkron
              </SortableHeader>
            </tr>
          </thead>
          <tbody>
            {!loading &&
              items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div className="dm-cell-stack dm-cell-stack--primary">
                      <strong>{item.itemName}</strong>
                      <span>{item.skuCode}</span>
                    </div>
                  </td>
                  <td>{item.warehouseName}</td>
                  <td className="dm-number-cell">
                    {quantityFormatter.format(item.quantity)}
                  </td>
                  <td>
                    <StockStatusBadge quantity={item.quantity} />
                  </td>
                  <td>{formatTimestamp(item.syncedAt)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </DataMasterTableShell>

      <DataMasterPagination
        loading={loading}
        onPageChange={(page) => load(page, filters, sort)}
        onPageSizeChange={changePageSize}
        pagination={pagination}
      />
    </DataMasterSection>
  );
}
